/**
 * HANNE BLOCH gift pop-up ("A GIFT FOR YOU"), rebuilt October 2026.
 *
 * Reads its markup from snippets/gift-floating-button.liquid (#hb-gift carries the settings).
 *
 * Link flows (the code is applied here through /cart/update.js, so the parameters survive):
 *   ?gift=GIFT-XXXXX                 shop flow: pick a bag, ADD TO MY ORDER, pop-up closes, stay on the page
 *   ?gift=GIFT-XXXXX&flow=checkout   checkout flow: pick a bag, ADD GIFT & GO TO CHECKOUT (or Continue
 *                                    shopping, which adds the bag and closes)
 *   ?gift=open                       just opens the pop-up (no code applied); ?drawstring=open is an alias
 * A gift code already on the cart (arrival through /discount/CODE?redirect=) opens the shop flow once
 * per session while no bag has been chosen. The flow is remembered for the session so the floating tab
 * and "Change gift" in the cart drawer reopen the same pop-up.
 *
 * states: gift (a code starting with the prefix is on the cart), purchasable (theme setting, bags at
 * their price), hidden (nothing renders). Nothing in here adds or removes a bag on its own.
 *
 * HB 2026-10-20 (Vic, gift link fixes 1 to 4):
 *   1. ?gift=CODE opens the pop-up at once; the code is applied in the background while she looks.
 *   2. If a paid piece is in the cart and the bag still is not complimentary after it is added (code used,
 *      expired or refused), the bag is taken out again, she is told in one line, and the button becomes
 *      CONTINUE TO CHECKOUT (Vic, later on 20 Oct: solve it in the pop-up, no email address).
 *   3. The code is added next to any code already on the cart instead of replacing it (only an older
 *      GIFT- code is swapped out, one gift per order).
 *   4. The button follows the cart, not the link: a paid piece in the cart gives ADD GIFT & GO TO CHECKOUT;
 *      an empty cart gives ADD GIFT TO MY ORDER, which adds the bag and lets her keep browsing.
 *      &flow=checkout in the links is still accepted and cleaned off, but no longer decides anything.
 *   5. With a gift code on the cart and no bag yet, the pop-up also opens (even if already seen this visit) when
 *      she arrives through a cart link (Shopify adds cart_link_id; the Abandoned Checkout emails use
 *      /cart/...?storefront=true) or comes back from checkout (the referrer is a /checkouts/ page). Checkout
 *      drops a gift code that does not apply yet, so the code is remembered for the visit and put back when
 *      she returns from checkout with her pieces and no bag.
 *   6. While a gift code is on the cart (waiting for its bag or applied), the "with orders over DKK 2,000" offer stands
 *      aside: the code is kept in the cart attribute _hb_gift_code (read by snippets/hb-tgift-calc for every render,
 *      and used to put the code back after checkout), and html.hb-gift-code-on hides the offer on the page at once.
 */
import { CartUpdateEvent } from '@theme/events';

const root = document.getElementById('hb-gift');
const panel = document.getElementById('freeGiftToast');

if (root && panel) {
  const state = {
    mode: 'hidden', // 'gift' | 'purchasable' | 'hidden', resolved from /cart.js
    hasPaid: root.dataset.hasPaid === 'true', // a paid piece in the cart (rendered by Liquid, then kept current)
    code: null, // the gift code from the link, kept to re-apply it if the cart lost it
    pendingCode: null, // the background code request, awaited before a bag is added
    userPicked: false, // she tapped a swatch: a late cart read must not move her choice
    resultShown: false, // a message after adding is on screen: cart updates must not reset the button
    nocodeMode: root.dataset.nocodeMode || 'hidden',
    prefix: (root.dataset.prefix || 'GIFT-').toUpperCase(),
    checkoutUrl: root.dataset.checkoutUrl || '/checkout',
    shipThresholdCents: Number(root.dataset.shipThresholdCents || 0),
    selected: null, // variant id of the bag shown on the stage
    busy: false,
    lastFocus: null,
    cartChanged: false // a bag was added while open; the cart drawer is told on close
  };

  const els = {
    status: panel.querySelector('[data-gift-status]'),
    primary: panel.querySelector('[data-gift-primary]'),
    primaryLabel: panel.querySelector('[data-gift-primary-label]'),
    secondary: panel.querySelector('[data-gift-secondary]'),
    close: panel.querySelectorAll('.popup__close_threshold'),
    tiles: Array.from(panel.querySelectorAll('[data-gift-tile]')),
    stageItems: Array.from(panel.querySelectorAll('[data-gift-stage-item]')),
    name: panel.querySelector('[data-gift-name]'),
    priceWrap: panel.querySelector('[data-gift-price-wrap]'),
    price: panel.querySelector('[data-gift-price]'),
    priceLabel: panel.querySelector('[data-gift-price-label]'),
    shipUnder: panel.querySelector('[data-ship-copy="under"]'),
    shipOver: panel.querySelector('[data-ship-copy="over"]'),
    tab: document.querySelector('[data-gift-tab]'),
    copies: panel.querySelectorAll('[data-gift-copy]')
  };

  const LABELS = {
    shop: 'Add gift to my order',
    checkout: 'Add gift & go to checkout',
    purchasable: 'Add to my order',
    close: 'Continue shopping',
    toCheckout: 'Continue to checkout'
  };

  // HB 2026-10-20 (Vic): while a gift code is on the cart, the threshold gift ("with orders over DKK 2,000": its module
  // in the cart, its mini line and its note) stands aside, at once on this page (the class below) and in every later
  // render (the cart attribute, read by snippets/hb-tgift-calc). One gift per order.
  const ATTR = '_hb_gift_code';
  const ON_CLASS = 'hb-gift-code-on';
  try {
    const css = document.createElement('style');
    css.textContent = `html.${ON_CLASS} .hb-tg-mod, html.${ON_CLASS} .hb-tg-mini, html.${ON_CLASS} [data-hb-tg-note] { display: none !important; }`;
    document.head.appendChild(css);
  } catch (e) {
    /* no head: nothing to hide */
  }

  // Option B (Vic, 2026-10-08): the GIFT code only applies once a paid piece is in the cart.
  const PRICE_LABEL = 'Complimentary';
  const PRICE_LABEL_FIRST = 'Complimentary with your first order'; // HB 2026-10-12 (Vic): first order, not first piece

  /* ---------- session memory ---------- */

  function remember(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      /* storage unavailable: the pop-up just forgets between pages */
    }
  }

  function recall(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  /* ---------- cart helpers ---------- */

  async function cartRequest(path, body) {
    const res = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.description || data?.message || `${path} failed`);
    return data;
  }

  async function getCart() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!res.ok) throw new Error('cart fetch failed');
      return await res.json();
    } catch (err) {
      console.error('[HB gift] cart fetch error', err);
      return null;
    }
  }

  function findGiftCode(cart) {
    const codes = cart?.discount_codes || [];
    return codes.find((dc) => String(dc.code || '').toUpperCase().startsWith(state.prefix)) || null;
  }

  function isBagLine(item) {
    return item?.properties?._free_gift === 'Yes';
  }

  // What the customer actually pays for, bag excluded: decides the shipping line.
  function paidSubtotalCents(cart) {
    return (cart?.items || [])
      .filter((item) => !isBagLine(item))
      .reduce((sum, item) => sum + (item.final_line_price || 0), 0);
  }

  // A paid piece: any line that is not the chosen bag and not one of the mini bags themselves
  // (a bag bought alongside does not make the GIFT code apply).
  const bagProductIds = new Set(els.tiles.map((t) => Number(t.dataset.productId)).filter(Boolean));
  function hasPaidPiece(cart) {
    return (cart?.items || []).some((item) => !isBagLine(item) && !bagProductIds.has(Number(item.product_id)));
  }

  function updatePriceLabel(cart) {
    if (!els.priceLabel || !cart) return; // no cart read: keep what the page rendered
    els.priceLabel.textContent = hasPaidPiece(cart) ? PRICE_LABEL : PRICE_LABEL_FIRST;
  }

  function updateShipping(cart) {
    if (!els.shipUnder || !els.shipOver || !state.shipThresholdCents) return;
    const over = paidSubtotalCents(cart) >= state.shipThresholdCents;
    els.shipUnder.hidden = over;
    els.shipOver.hidden = !over;
  }

  // Fix 3: /cart/update.js { discount } replaces every code on the cart, so the codes already there are
  // sent along (comma separated). Only an older gift code is dropped: one gift per order.
  // Returns the updated cart, or null when the request failed.
  async function applyCode(code) {
    try {
      const cart = await getCart();
      const wanted = String(code).trim();
      const keep = (cart?.discount_codes || [])
        .map((dc) => String(dc.code || '').trim())
        .filter((c) => c && c.toUpperCase() !== wanted.toUpperCase() && !c.toUpperCase().startsWith(state.prefix));
      // The cart attribute _hb_gift_code tells Liquid a gift code is on (a waiting code is invisible to Liquid), so the
      // "with orders over DKK 2,000" offer stands aside (snippets/hb-tgift-calc, HB 2026-10-20).
      return await cartRequest('/cart/update.js', {
        discount: keep.concat(wanted).join(','),
        attributes: { [ATTR]: wanted }
      });
    } catch (err) {
      console.error('[HB gift] could not apply the gift code', err);
      return null;
    }
  }

  // The bag line is complimentary once its price after discounts is 0 (the GIFT code, or the store's
  // automatic gift above its threshold).
  function bagIsFree(cart) {
    const bag = (cart?.items || []).find(isBagLine);
    if (!bag) return false;
    return Number(bag.final_line_price) === 0;
  }

  async function removeBagLine(cart) {
    const bag = (cart?.items || []).find(isBagLine);
    if (!bag) return;
    await cartRequest('/cart/change.js', { id: bag.key, quantity: 0 });
  }

  // A gift code that does not apply is taken off again (any other code stays), so checkout shows no
  // refused code. Errors are swallowed: at worst the code stays on, which checkout ignores.
  async function dropGiftCode() {
    try {
      const cart = await getCart();
      const keep = (cart?.discount_codes || [])
        .map((dc) => String(dc.code || '').trim())
        .filter((c) => c && !c.toUpperCase().startsWith(state.prefix));
      await cartRequest('/cart/update.js', { discount: keep.join(','), attributes: { [ATTR]: '' } });
    } catch (err) {
      console.error('[HB gift] could not take the gift code off', err);
    }
  }

  /* ---------- UI helpers ---------- */

  function setStatus(text) {
    if (!els.status) return;
    els.status.textContent = text || '';
    els.status.hidden = !text;
  }

  function setPrimary(label, handler) {
    if (!els.primary) return;
    els.primaryLabel.textContent = label;
    els.primary.hidden = false;
    els.primary.onclick = handler;
  }

  function setSecondary(visible) {
    if (!els.secondary) return;
    els.secondary.hidden = !visible;
  }

  function setBusy(flag) {
    state.busy = flag;
    panel.classList.toggle('is-busy', flag);
    if (els.primary) els.primary.disabled = flag;
  }

  function select(variantId) {
    state.selected = String(variantId);
    els.tiles.forEach((tile) => {
      const on = tile.dataset.variantId === state.selected;
      tile.classList.toggle('is-selected', on);
      tile.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on && els.name) els.name.textContent = tile.dataset.productTitle || '';
      if (on && els.price) els.price.textContent = tile.dataset.price || '';
    });
    els.stageItems.forEach((item) => {
      item.hidden = item.dataset.variantId !== state.selected;
    });
  }

  function selectDefault(cart) {
    // The bag already in the cart wins, else the first bag that can be added.
    const inCart = (cart?.items || []).find(isBagLine);
    const fromCart = inCart && els.tiles.find((t) => Number(t.dataset.variantId) === Number(inCart.variant_id) && !t.disabled);
    const first = els.tiles.find((t) => !t.disabled);
    const pick = fromCart || first;
    if (pick) select(pick.dataset.variantId);
  }

  // Fix 4: the button follows the cart. A paid piece in it: add the gift and go to checkout.
  // Nothing paid yet: add the gift and keep browsing.
  function applyFlowUI() {
    const gift = state.mode === 'gift';
    const checkoutFlow = gift && state.hasPaid;
    state.resultShown = false;
    if (els.priceWrap) els.priceWrap.style.display = ''; // the theme styles it display:flex, so [hidden] alone does not hide it
    setPrimary(checkoutFlow ? LABELS.checkout : gift ? LABELS.shop : LABELS.purchasable, onPrimary);
    setSecondary(false);
    setStatus('');
  }

  // A fresh cart read: price line, shipping line and, unless a message is on screen, the button.
  function refresh(cart) {
    if (!cart) return;
    state.hasPaid = hasPaidPiece(cart);
    updateShipping(cart);
    updatePriceLabel(cart);
    if (!state.resultShown && !state.busy) applyFlowUI();
  }

  /* ---------- open / close ---------- */

  // The theme's cart drawer is a <dialog> in the browser's top layer; anything opened while it is
  // up renders underneath it, so any open drawer is closed before the pop-up shows. (A top-layer
  // <dialog> for the pop-up itself was tried and dropped: the bag images did not paint inside it.)
  function closeOtherDialogs() {
    document.querySelectorAll('dialog[open]').forEach((dlg) => {
      if (dlg === panel) return;
      const host = dlg.closest('cart-drawer-component');
      if (host && typeof host.close === 'function') host.close();
      else if (typeof dlg.close === 'function') dlg.close();
      // The theme closes its drawer after a slide-out animation. If that animation never ends
      // (background tab, animations off), close the dialog ourselves so it cannot sit on top.
      setTimeout(() => {
        if (!dlg.open) return;
        dlg.classList.remove('dialog-closing');
        if (typeof dlg.close === 'function') dlg.close();
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
      }, 700);
    });
  }

  function openPopup() {
    if (panel.classList.contains('is-visible')) return;
    state.lastFocus = document.activeElement;
    closeOtherDialogs();
    // The bag images are lazy so a hidden pop-up costs nothing; once it opens, fetch all of them
    // (the hidden stage items too) so switching swatches never shows an empty stage.
    panel.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    applyFlowUI();
    getCart().then(refresh);
    panel.classList.add('is-visible');
    document.body.classList.add('hb-gift-open');
    const first = panel.querySelector('.hb-gift-swatch.is-selected') || panel.querySelector('.popup__close_threshold');
    first?.focus({ preventScroll: true });
  }

  function closePopup() {
    panel.classList.remove('is-visible');
    document.body.classList.remove('hb-gift-open');
    setStatus('');
    if (state.lastFocus && typeof state.lastFocus.focus === 'function') state.lastFocus.focus({ preventScroll: true });
    if (state.cartChanged) {
      state.cartChanged = false;
      document.dispatchEvent(new CartUpdateEvent({ source: 'hb-gift-popup' }));
    }
  }

  /* ---------- actions ---------- */

  async function addBag(variantId) {
    const payload = { id: Number(variantId), quantity: 1 };
    if (state.mode === 'gift') {
      payload.properties = { _free_gift: 'Yes' };
      // One bag per order: swap an existing bag line for the new choice.
      const cart = await getCart();
      const existing = (cart?.items || []).find(isBagLine);
      if (existing && Number(existing.variant_id) === Number(variantId)) return cart;
      if (existing) await cartRequest('/cart/change.js', { id: existing.key, quantity: 0 });
    }
    return cartRequest('/cart/add.js', payload);
  }

  async function addSelected() {
    if (state.busy || !state.selected) return;
    setStatus('');
    setBusy(true);
    try {
      // The code from the link may still be on its way to the cart (fix 1): wait for it first.
      if (state.pendingCode) await state.pendingCode;

      await addBag(state.selected);
      state.cartChanged = true;

      let cart = await getCart();
      // The cart lost the gift code (another tab, a cleared cart): put it back once.
      if (state.mode === 'gift' && state.code && !findGiftCode(cart)) {
        cart = (await applyCode(state.code)) || cart;
      }
      state.hasPaid = hasPaidPiece(cart);
      updateShipping(cart);
      updatePriceLabel(cart);

      // Purchasable bags, or a gift with nothing paid in the cart yet: the bag is in, she keeps browsing.
      // (With nothing paid the code cannot apply yet; the price line says "with your first order".)
      if (state.mode !== 'gift' || !state.hasPaid) {
        closePopup();
        return;
      }

      if (bagIsFree(cart)) {
        state.resultShown = true;
        setStatus('Taking you to checkout.');
        window.location.assign(state.checkoutUrl);
        return;
      }

      // Fix 2: a paid piece is in the cart and the bag still carries its price, so the code does not
      // apply (used, expired or refused). Take the bag back out rather than charge for a "gift", say so in
      // one line and let her carry on to checkout from here (Vic: solved in the pop-up, no email address).
      // No amounts here: the discount settings are a back-office matter.
      await removeBagLine(cart);
      await dropGiftCode();
      state.code = null;
      remember('hb_gift_code', ''); // a refused code is not put back when she returns from checkout
      document.documentElement.classList.remove(ON_CLASS);
      state.resultShown = true;
      if (els.priceWrap) els.priceWrap.style.display = 'none'; // no "Complimentary" next to a bag that is not coming
      setStatus('Your gift code does not apply to this order, so we have left the bag out. Your pieces are still in your cart.');
      setPrimary(LABELS.toCheckout, (ev) => {
        ev?.preventDefault?.();
        window.location.assign(state.checkoutUrl);
      });
      setSecondary(false);
    } catch (err) {
      console.error('[HB gift] add error', err);
      setStatus('Something went wrong adding the bag. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  function onPrimary(e) {
    e?.preventDefault?.();
    addSelected();
  }

  /* ---------- state ---------- */

  function applyMode(mode) {
    state.mode = mode;
    root.dataset.state = mode;
    els.copies.forEach((el) => {
      el.hidden = el.dataset.giftCopy !== mode;
    });
    if (els.tab) els.tab.hidden = mode === 'hidden';
    // Gift state: the market price struck through beside "Complimentary" (option F).
    // Purchasable state: the plain price.
    if (els.priceWrap) els.priceWrap.classList.toggle('is-gift', mode === 'gift');
    document.documentElement.classList.toggle(ON_CLASS, mode === 'gift');
  }

  async function resolveMode() {
    const cart = await getCart();
    const code = findGiftCode(cart);
    if (code) {
      // Remember the code for this visit: checkout drops it while no bag is in the cart (fix 5).
      state.code = String(code.code);
      remember('hb_gift_code', state.code);
      if (String(cart?.attributes?.[ATTR] || '') !== state.code) {
        cartRequest('/cart/update.js', { attributes: { [ATTR]: state.code } }).catch(() => {});
      }
      return { mode: 'gift', cart };
    }
    if (state.nocodeMode === 'purchasable') return { mode: 'purchasable', cart };
    return { mode: 'hidden', cart };
  }

  /* ---------- wiring ---------- */

  els.tiles.forEach((tile) =>
    tile.addEventListener('click', () => {
      if (tile.disabled || state.busy) return;
      state.userPicked = true;
      select(tile.dataset.variantId);
      if (state.resultShown) applyFlowUI();
      else setStatus('');
    })
  );
  els.close.forEach((el) =>
    el.addEventListener('click', (e) => {
      e.preventDefault();
      closePopup();
    })
  );
  if (els.secondary) {
    els.secondary.addEventListener('click', (e) => {
      e.preventDefault();
      addSelected();
    });
  }
  panel.addEventListener('click', (e) => {
    if (e.target === panel) closePopup();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && panel.classList.contains('is-visible')) closePopup();
  });
  // Keep the price line current while the page is open (a piece added or removed in the cart), so the
  // pop-up never opens with yesterday's wording.
  document.addEventListener(CartUpdateEvent.eventName || 'cart:update', (e) => {
    if (state.mode !== 'gift' || e.detail?.resource?.source === 'hb-gift-popup') return; // our own close: already current
    getCart().then(refresh);
  });
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-gift-open], .gift-floating-btn, .change-gift');
    if (!trigger) return;
    // Always swallow the click: "Change gift" sits inside the cart form and must never submit it.
    e.preventDefault();
    if (state.mode === 'hidden') return;
    openPopup();
  });

  /* ---------- boot ---------- */

  (async () => {
    const url = new URL(window.location.href);
    const giftParam = (url.searchParams.get('gift') || '').trim();
    const flowParam = (url.searchParams.get('flow') || '').trim().toLowerCase();
    const legacyOpen = url.searchParams.get('drawstring') === 'open';
    const codeParam = giftParam && giftParam.toLowerCase() !== 'open' ? giftParam : '';
    const forced = Boolean(codeParam) || giftParam.toLowerCase() === 'open' || legacyOpen;

    // Clean the parameters off the address bar so a reload or a share does not replay them.
    if (giftParam || flowParam || legacyOpen) {
      ['gift', 'flow', 'drawstring'].forEach((k) => url.searchParams.delete(k));
      try {
        window.history.replaceState(window.history.state, '', url.pathname + (url.search || '') + url.hash);
      } catch (e) {
        /* ignore */
      }
    }

    // Fix 1: a gift code in the link opens the pop-up straight away, before any request. The code is
    // applied in the background; the button and price line follow once the cart answers.
    if (codeParam) {
      state.code = codeParam;
      remember('hb_gift_code', codeParam);
      remember('hb_gift_seen', '1');
      applyMode('gift');
      selectDefault(null);
      openPopup();
      state.pendingCode = applyCode(codeParam);
      const applied = await state.pendingCode;
      state.pendingCode = null;
      const cart = applied || (await getCart());
      if (!state.userPicked) selectDefault(cart);
      refresh(cart);
      return;
    }

    state.code = recall('hb_gift_code');

    // Fix 5: through a cart link (the Abandoned Checkout emails) or back from checkout, she sees the choice again
    // even if the pop-up was already shown this visit, as long as no bag is in the cart yet.
    const fromCartLink = url.searchParams.has('cart_link_id');
    let fromCheckout = false;
    try {
      const ref = document.referrer ? new URL(document.referrer) : null;
      fromCheckout = Boolean(ref && ref.host === window.location.host && ref.pathname.includes('/checkouts/'));
    } catch (e) {
      /* no usable referrer */
    }

    let { mode, cart } = await resolveMode();

    // Checkout drops a gift code that does not apply yet (no bag in the cart), so back from checkout the code
    // may be gone. With her pieces still in the cart, no bag, and the code remembered from this visit, put it
    // back so the choice can be shown. (Nothing paid in the cart, as after an order: leave it.)
    const attrCode = String(cart?.attributes?.[ATTR] || '').trim();
    const backCode = attrCode || (fromCheckout ? state.code : '');
    if (mode !== 'gift' && backCode && cart && hasPaidPiece(cart) && !(cart.items || []).some(isBagLine)) {
      state.code = backCode;
      const applied = await applyCode(backCode);
      if (applied && findGiftCode(applied)) {
        cart = applied;
        mode = 'gift';
      }
    }

    applyMode(mode);
    selectDefault(cart);
    if (cart) state.hasPaid = hasPaidPiece(cart);
    updateShipping(cart);
    updatePriceLabel(cart);
    if (mode === 'hidden') return;

    const hasBag = (cart?.items || []).some(isBagLine);
    const auto = mode === 'gift' && !hasBag && (recall('hb_gift_seen') !== '1' || fromCartLink || fromCheckout);
    if (forced || auto) {
      remember('hb_gift_seen', '1');
      openPopup();
    }
  })();
}
